export default (() =>
    class extends wealthify.componentBases.WebComponent {

        constructor (config = {}) {
            // Run initial construction
            config.delayConstruction = true;
            super(config);

            // Add config overrides and complete construction
            config.templateHTML = this.#getUpdatedTemplate(config.templateHTML || this.constructor.template);
            config.cacheDom?.push('data-modal', 'data-fieldlabelbutton');

            this.canHaveMultipleInputs = config.canHaveMultipleInputs ?? false;
            this.isFieldInputConstructionDelayed = config.isFieldInputConstructionDelayed ?? false;

            this.initialiseConstruction(config);

            this.styleSelectors.add(this.constructor.fieldBaseStyleSelector);

            if (this.styleSelector) {
                this.styleSelectors.add(this.styleSelector);
            }

            this.onConnectedCallback = () => {
                if(this.isFieldInputConstructionDelayed){
                    this.addEventListener(this.events.InstanceLoaded.name, this.#runConnectedCallback);
                } else {
                    this.#runConnectedCallback();
                }
            };
        }

        // Public methods

        checkValidity() {
            return this.#internals.checkValidity();
        }

        reportValidity() {
            return this.#internals.reportValidity();
        }

        // Private properties

        #originalValue;

        get #fieldInput () {
            // Return first item if multiple inputs
            const firstInput = Array.isArray(this.dom.fieldinput) ? this.dom.fieldinput[0] : this.dom.fieldinput;

            return firstInput;
        }

        get #fieldInputs () {
            if (!this.dom.fieldinput) {
                return [];
            }

            // if  already an array then convert it to an array
            return Array.isArray(this.dom.fieldinput) ? this.dom.fieldinput : [this.dom.fieldinput];
        }

        get isAttachInternalsSupported () {
            return this.attachInternals !== undefined;
        }

        get #internals () {
            if (!this.isAttachInternalsSupported) {
                // TODO: Remove once supported
                const polyfilledFunctions = {
                    setFormValue: () => {},
                    setValidity: () => {}
                };

                Object.assign(this.#fieldInput, polyfilledFunctions);

                return this.#fieldInput;
            }

            // Invoke and assign attachInternals unless it has already been called
            this.internals_ = this.internals_ ?? this.attachInternals();

            return this.internals_;
        }

        // Static properties

        static fieldBaseStyleSelector = 'c-field-base';
        static formAssociated = true;

        static baseValidationProps = [
            wealthify.FieldValidation.validationPropEnums.Custom,
            wealthify.FieldValidation.validationPropEnums.Required,
        ];

        static eventSettings = Object.freeze({events: {
            InstanceLoaded: 'instance-loaded',
            ValueChange: wealthify.ComponentEvents.globalEvents.ValueChanged,
            DisabledToggled: wealthify.ComponentEvents.globalEvents.DisabledToggled,
            ReadOnlyToggled: 'readonlytoggled',
            ValidationChanged: 'validationchanged', // derived from validation events.
            OriginalValueChange: 'originalvaluechange' // to update the value used for isDirty
        }});

        // Instance properties

        get key () {
            // Added for legacy support
            // TODO: Remove when no longer used
            return this.name;
        }

        get form () {
            return this.#internals.form;
        }

        get type () {
            return this.#fieldInput.type ?? this.#fieldInput.tagName.toLowerCase();
        }

        get validity () {
            return this.#internals.validity;
        }

        get validationMessage () {
            // Returns breaking messages from form-associated internals
            // Note: will not return warnings, use this.validation.message instead
            // Also: if setValidity hasn't been run yet, rely on this.validation.breakingMessage
            return this.#internals.validationMessage;
        }

        get willValidate () {
            return this.#internals.willValidate;
        }

        get valid () {
            return this.validation?.valid;
        }

        get isDirty () {
            return this.value.toString() !== this.#originalValue.toString();
        }

        inputData = {};
        validation;
        canHaveMultipleInputs = false;
        isFieldInputConstructionDelayed = false;

        fieldProps = Object.freeze([
            {
                class: new wealthify.Prop('name', {default: `k${wealthify.type.number.getRandom(10)}`}),
                callback: () => this.#setName()
            },
            {
                class: new wealthify.Prop('value', {default: ''}),
                callback: () => {
                    // Clone inputData so it's not present if value change requires nested value changes
                    const data = Object.keys(this.inputData).length !== 0
                        ? wealthify.type.object.deepCopy(this.inputData)
                        : {};

                    this.inputData = {};
                    this.events.ValueChange.dispatch(data);
                }
            },

            {
                class: new wealthify.Prop('readonlyprop', {default: false}),
                callback: () => this.#updateReadOnly()
            },
            {
                class: wealthify.props.Disabled(),
                callback: () => this.#updateDisabled()
            },

            {
                class: new wealthify.Prop('label', {required: true}),
                callback: () => this.#updateLabel()
            },
            {
                class: new wealthify.Prop('labelpre', {default: ''}),
                callback: () => this.#updateLabelPre()
            },
            {
                class: new wealthify.Prop('labelsub', {default: ''}),
                callback: () => this.#updateLabelSub()
            },
            {
                class: new wealthify.Prop('is-label-hidden', {
                    default: false,
                    type: wealthify.propTypes.Boolean()
                }),
                callback: () => this.#toggleLabelVisibility()
            }
        ]);

        // Private methods

        get #hasModal () {
            return this.dom.modal && this.dom.modal.tagName.toLowerCase() === 'w-modal';
        }

        #runConnectedCallback () {
            const validationProps = [
                ...(this.constructor.validationProps ? this.constructor.validationProps : []),
                ...this.constructor.baseValidationProps
            ];

            this.validation = new wealthify.FieldValidation(this, validationProps);

            this.deprecatedAttachAdditionalProps(this.fieldProps);
            this.#updateValidity();

            this.#addSelectorsToNodes();
            this.#hideNodesByDefault();
            this.#linkLabelsToInput();
            this.#setUpModal();
            this.#updateMessageUI();

            this.#originalValue = this.value;
            this.dataset.fieldComponent = '';

            this.addEventListener(this.validation.events.StatusChange.name, this.#updateInternalsAndMessageUI);
            this.addEventListener(this.validation.events.MessageChange.name, this.#updateInternalsAndMessageUI);
            this.addEventListener(this.events.ValueChange.name, this.#updateInternalsAndMessageUI);
            this.addEventListener(this.events.DisabledToggled.name, this.#disableLabelButton);
            this.addEventListener(this.validation.events.RequiredToggled.name, this.#updateLabel);
            this.addEventListener('click', this.#focusOnLabelClick);
            this.addEventListener('blur', this.validation.events.StatusDismiss.dispatch, true);
            this.addEventListener(this.events.OriginalValueChange.name, () => this.#originalValue = this.value);

            // Dispatch value change to give the instance an opportunity
            // to make the values of input and property consistent
            this.events.ValueChange.dispatch();
            this.isInstanceLoaded = true;
        }

        #getUpdatedTemplate (configTemplate) {
            const template = new DOMParser().parseFromString(configTemplate, 'text/html').body;
            const labelContainerNode = template.querySelector('[data-fieldlabelcontainer]');

            template.querySelector('[data-root]').appendChild(this.#getModalDOM());
            labelContainerNode.appendChild(this.#getModalButtonDOM());

            return template;
        }

        #getModalDOM () {
            const modalSlot = document.createElement('span');

            modalSlot.dataset.slot = 'modal';
            modalSlot.dataset.modal = '';
            modalSlot.classList.add(`${this.constructor.fieldBaseStyleSelector}__modal`);

            return modalSlot;
        }

        #getModalButtonDOM () {
            const button = wealthify.dom.fromString('<w-button-2></w-button-2>');

            button.classList.add('u-display__none');
            button.classList.add(`${this.constructor.fieldBaseStyleSelector}__modal-button`);
            button.size = 'small';
            button.label = 'More info';
            button.iconright = 'question-circle';
            button.variant = 'tertiary';
            button.isInlineMobile = true;
            button.dataset.fieldlabelbutton = '';

            return button;
        }

        #addSelectorsToNodes () {
            this.dom.fieldlabelcontainer.classList.add(`${this.constructor.fieldBaseStyleSelector}__label-container`);
            this.dom.fieldlabel.classList.add(`${this.constructor.fieldBaseStyleSelector}__label`);
            this.dom.fieldlabelpre.classList.add(`${this.constructor.fieldBaseStyleSelector}__label-pre`);
            this.dom.fieldlabelsub.classList.add(`${this.constructor.fieldBaseStyleSelector}__label-sub`);
            this.dom.fieldmessage.classList.add(`${this.constructor.fieldBaseStyleSelector}__message`);
        }

        #hideNodesByDefault () {
            const labelClassListAction = this.label && !this.isLabelHidden ? 'remove' : 'add';
            const labelpreClassListAction = this.labelpre && !this.isLabelHidden ? 'remove' : 'add';
            const labelsubClassListAction = this.labelsub && !this.isLabelHidden
                ? 'remove' : 'add';
            const messageClassListAction = this.validation.message ? 'remove' : 'add';

            this.dom.fieldlabel.classList[labelClassListAction]('u-display__none');
            this.dom.fieldlabelpre.classList[labelpreClassListAction]('u-display__none');
            this.dom.fieldlabelsub.classList[labelsubClassListAction]('u-display__none');

            this.dom.fieldmessage.classList[messageClassListAction]('u-display__none');
        }

        #linkLabelsToInput () {
            const preLabelId = `${this.name}-pre-label`;
            const subLabelId = `${this.name}-sub-label`;
            const messageId = `${this.name}-message`;
            const inputId = `${this.name}-input`;

            this.dom.fieldlabelpre.id = preLabelId;
            this.dom.fieldlabelsub.id = subLabelId;
            this.dom.fieldmessage.id = messageId;

            if (this.canHaveMultipleInputs) {
                // Fields with multiple inputs are contained within a fieldset, and the inputs are
                // collectively labelled by a legend element. Because the optional sub label and
                // message elements are outside of the legend, we need to reference them in the
                // aria-describedby attribute on the fieldset
                this.dom.fieldset.setAttribute('aria-describedby', `${subLabelId} ${messageId}`);
            } else {
                this.dom.fieldlabel.setAttribute('for', inputId);
                this.#fieldInput.id = inputId;
                this.#fieldInput.setAttribute('aria-describedby', `${preLabelId} ${subLabelId} ${messageId}`);
            }
        }

        #setUpModal () {
            if (this.#hasModal) {
                this.dom.fieldlabelbutton.addEventListener('click', () => this.dom.modal.open = true);
                this.dom.fieldlabelbutton.classList.remove('u-display__none');
            }
        }

        #setName () {
            const mainFormElement = this.isAttachInternalsSupported ? this : this.dom.fieldinput;

            mainFormElement.setAttribute('id', this.name);

            if (!this.isAttachInternalsSupported) {
                mainFormElement.setAttribute('name', this.name);
            }
        }

        #updateDisabled () {
            this.#updateFieldInputStatusProp(this.disabledprop, 'disabled');
            this.events.DisabledToggled.dispatch();
        }

        #updateReadOnly () {
            this.#updateFieldInputStatusProp(this.readonlyprop, 'readonly');
            this.#updateFieldLabelReadonlyStyle();
            this.events.ReadOnlyToggled.dispatch();

            // Optional will be removed from the label if readonly
            this.#updateLabel();
        }

        #updateFieldLabelReadonlyStyle() {
            if(this.dom.fieldlabel){
                const fieldLabelClassListAction = this.readonlyprop ? 'add' : 'remove';

                this.dom.fieldlabel.classList[fieldLabelClassListAction](`${this.constructor.fieldBaseStyleSelector}__label--readonly`);
            }
        }

        #updateFieldInputStatusProp (value, attribute) {
            const classListAction = value ? 'add' : 'remove';
            const prop = `${attribute}prop`;
            const fieldElements = Array.isArray(this.dom.fieldinput) ? this.dom.fieldinput : [this.#fieldInput];

            if (this[prop]) {
                fieldElements.forEach((element) => element.setAttribute(attribute, true));
            } else {
                fieldElements.forEach((element) => element.removeAttribute(attribute));
            }

            this.styleSelectors.forEach((styleSelector) => this.classList[classListAction](`${styleSelector}--${attribute}`));

            if (value) {
                this.validation.events.StatusDismissAll.dispatch();
            }
        }

        #updateLabel () {
            if (!this.label === undefined || !this.dom.fieldlabel) {
                return;
            }

            const label = this.label + (!this.validateRequired && !this.readonlyprop ? ' (Optional)' : '');

            if (this.#hasModal) {
                // Button will be added alongside the label when modal is present
                // so ensure it is centered

                this.dom.fieldlabel.innerHTML = `<span class="u-text__line-height--compensate-for-center">${label}</span>`;
            } else {
                this.dom.fieldlabel.textContent = label;
            }
        }

        #updateLabelPre () {
            if (!this.labelpre === undefined || !this.dom.fieldlabelpre) {
                return;
            }

            const classListAction = this.labelpre === '' ? 'add' : 'remove';

            this.dom.fieldlabelpre.textContent = this.labelpre;
            this.dom.fieldlabelpre.classList[classListAction]('u-display__none');
        }

        #updateLabelSub () {
            if (!this.labelsub === undefined || !this.dom.fieldlabelsub) {
                return;
            }

            const classListAction = this.labelsub === '' ? 'add' : 'remove';

            this.dom.fieldlabelsub.textContent = this.labelsub;
            this.dom.fieldlabelsub.classList[classListAction]('u-display__none');
        }

        #toggleLabelVisibility() {
            const hideLabelClassListAction = this.label === '' || this.isLabelHidden ? 'add' : 'remove';
            const hideLabelPreClassListAction = this.labelpre === '' || this.isLabelHidden ? 'add' : 'remove';
            const hideLabelSubClassListAction = this.labelsub === '' || this.isLabelHidden ? 'add' : 'remove';

            this.dom.fieldlabel.classList[hideLabelClassListAction]('u-display__none');
            this.dom.fieldlabelpre.classList[hideLabelPreClassListAction]('u-display__none');
            this.dom.fieldlabelsub.classList[hideLabelSubClassListAction]('u-display__none');

            if (this.isLabelHidden) {
                const labels = [this.labelpre, this.label, this.labelsub].filter((label) => label !== '');

                this.#fieldInputs.forEach(input => input.setAttribute('aria-label', labels.join(', ')));
            } else {
                this.#fieldInputs.forEach(input => input.removeAttribute('aria-label'));
            }
        }

        #updateInternalsAndMessageUI () {
            this.#internals.setFormValue(this.value);
            this.#updateValidity();
            this.#updateMessageUI();
        }

        #updateMessageUI () {
            const classListAction = this.validation.message === '' ? 'add' : 'remove';

            this.dom.fieldmessage.label = this.validation.message;
            this.dom.fieldmessage.type = this.validation.valid ? 'warning' : 'error';
            this.dom.fieldmessage.classList[classListAction]('u-display__none');
        }

        #disableLabelButton () {
            this.dom.fieldlabelbutton.disabledprop = this.disabledprop;
        }

        #focusOnLabelClick (event) {
            const validClickableElements = [
                this.dom.fieldlabel,
                this.dom.fieldlabelpre,
                this.dom.fieldlabelsub,
                this.dom.controlscontainer,
            ];
            const isLabelClicked = validClickableElements.includes(event.target);

            if (isLabelClicked) {
                this.#fieldInput.focus();
            }
        }

        #updateValidity () {
            // Validation message is empty but valid flag is false
            // can often be the case when the element hasn't been interacted with yet
            const notReadyForValidityToBeSet = this.validation.breakingMessage === '' && !this.valid;
            // Validity of input and component are mismatched so input validity cannot be relied upon
            // can often be the case if component has polyfilled props
            const hasMismatchDueToPolyfilledProps = this.valid && !this.#fieldInput.validity.valid;

            if (notReadyForValidityToBeSet || hasMismatchDueToPolyfilledProps) {
                return;
            }

            this.#internals.setValidity(this.#fieldInput.validity, this.validation.breakingMessage);
        }
    }
);